// Dynamic SMART Bench Manager - Adaptive Wait Distribution for P=4C+5 to 8C
// UPDATED: Added initial phase priority to ensure all players get first game before anyone gets second
class SmartBenchManager {
  constructor() {
    this.sessionPartnerships = new Map();
    this.gameNumber = 1;
    this.playerWaitHistory = new Map();
    this.committedNextUp = null;
    
    // NEW: Initial phase tracking
    this.initialBenchPlayers = new Set(); // Players who started on bench
    this.initialPhaseActive = true; // Whether we're still in initial fairness phase
    
    console.log('Dynamic SMART Manager initialized - supports 4C+5 to 8C players with initial phase priority');
  }

  // NEW: Initialize the system with knowledge of who started on bench
  initializeSession(allPlayers, initialCourtPlayers, initialNextUp) {
    console.log('Initializing SMART session with initial phase tracking');
    
    // Track who was on courts initially
    const initialCourtIds = initialCourtPlayers.flat().map(p => p.id);
    const initialNextUpIds = initialNextUp.map(p => p.id);
    
    // Everyone else started on bench
    allPlayers.forEach(player => {
      if (!initialCourtIds.includes(player.id) && !initialNextUpIds.includes(player.id)) {
        this.initialBenchPlayers.add(player.id);
        console.log(`Initial bench player: ${player.name}`);
      }
    });
    
    console.log(`Initial phase: ${this.initialBenchPlayers.size} players need their first game`);
    this.initialPhaseActive = this.initialBenchPlayers.size > 0;
  }

  // CORE METHOD: Select next 4 players with initial phase priority
  selectNext4(bench, exitingPlayers = [], allPlayers = []) {
    if (!bench || bench.length === 0) {
      console.warn('⚠️ No players in bench');
      return { players: [], partnerships: null, formatted: 'No players available' };
    }
    
    console.log(`${this.initialPhaseActive ? 'INITIAL PHASE' : 'FULL SMART'} selection for Game ${this.gameNumber}`);
    console.log(`Bench: ${bench.length}, Exiting: ${exitingPlayers.length}`);
    
    // Check if we should transition out of initial phase
    this.checkInitialPhaseTransition(allPlayers);
    
    // If this is just a display calculation (no exiting players), return current commitment or calculate new
    if (!exitingPlayers || exitingPlayers.length === 0) {
      return this.getDisplayNextUp(bench, allPlayers);
    }
    
    // If we have exiting players, this is a rotation event
    console.log('Processing rotation with exiting players:', exitingPlayers.map(p => p.name).join(', '));
    
    // Step 1: Honor the committed Next Up (they move to court)
    const playersMovingToCourt = this.committedNextUp ? this.committedNextUp.players : [];
    console.log('Committed players moving to court:', playersMovingToCourt.map(p => p.name).join(', '));
    
    // Step 2: Calculate NEW Next Up for future display
    const newNextUp = this.calculateNewNextUp(bench, playersMovingToCourt, allPlayers);
    
    // Step 3: Update committed Next Up for future display
    this.committedNextUp = newNextUp;
    
    return {
      players: playersMovingToCourt,
      partnerships: this.committedNextUp.partnerships,
      formatted: this.committedNextUp.formatted
    };
  }

  // NEW: Check if we should transition from initial phase to full SMART
  checkInitialPhaseTransition(allPlayers) {
    if (!this.initialPhaseActive) return;
    
    // Check if any initial bench players still haven't played
    const unplayedInitialPlayers = allPlayers.filter(player => 
      this.initialBenchPlayers.has(player.id) && (player.gamesPlayed || 0) === 0
    );
    
    if (unplayedInitialPlayers.length === 0) {
      console.log('INITIAL PHASE COMPLETE - All initial bench players have played their first game');
      console.log('Transitioning to FULL SMART rotation system');
      this.initialPhaseActive = false;
    } else {
      console.log(`Initial phase continues: ${unplayedInitialPlayers.length} players still need first game:`, 
        unplayedInitialPlayers.map(p => p.name).join(', '));
    }
  }

  // Get Next Up for display purposes (no rotation event)
  getDisplayNextUp(bench, allPlayers) {
    if (this.committedNextUp) {
      console.log('Returning committed Next Up:', this.committedNextUp.players.map(p => p.name).join(', '));
      return this.committedNextUp;
    }
    
    // First time calculation - no commitment yet
    const nextUp = this.calculateNewNextUp(bench, [], allPlayers);
    this.committedNextUp = nextUp;
    return nextUp;
  }

  // Calculate new Next Up using initial phase priority or full SMART algorithm
  calculateNewNextUp(bench, movingPlayers, allPlayers) {
    console.log(`Calculating NEW Next Up (${this.initialPhaseActive ? 'INITIAL PHASE' : 'FULL SMART'})...`);
    
    // Remove moving players from available pool
    const movingPlayerIds = movingPlayers.map(p => p.id);
    const availableBench = bench.filter(p => !movingPlayerIds.includes(p.id));
    
    if (availableBench.length < 4) {
      console.warn(`Insufficient bench players: need 4, have ${availableBench.length}`);
      return { players: availableBench, partnerships: null, formatted: 'Insufficient players' };
    }
    
    let selectedPlayers;
    
    if (this.initialPhaseActive) {
      // INITIAL PHASE: Prioritize players who haven't played yet
      selectedPlayers = this.selectFromBenchInitialPhase(availableBench, 4, allPlayers);
    } else {
      // FULL SMART PHASE: Use standard SMART algorithm
      selectedPlayers = this.selectFromBench(availableBench, 4, allPlayers);
    }
    
    console.log('NEW Next Up calculated:', selectedPlayers.map(p => p.name).join(', '));
    
    // Assign optimal partnerships
    return this.assignOptimalPartnerships(selectedPlayers);
  }

  // NEW: Selection algorithm for initial phase - prioritizes first-time players
  selectFromBenchInitialPhase(bench, count, allPlayers) {
    console.log('INITIAL PHASE SELECTION: Prioritizing first-time players');
    
    // Separate initial bench players who haven't played from others
    const firstTimePlayers = bench.filter(player => 
      this.initialBenchPlayers.has(player.id) && (player.gamesPlayed || 0) === 0
    );
    
    const experiencedPlayers = bench.filter(player => 
      !this.initialBenchPlayers.has(player.id) || (player.gamesPlayed || 0) > 0
    );
    
    console.log(`Available: ${firstTimePlayers.length} first-time, ${experiencedPlayers.length} experienced`);
    
    let selectedPlayers = [];
    
    // Priority 1: Fill with first-time players
    if (firstTimePlayers.length >= count) {
      // We have enough first-time players, select based on original bench order or simple criteria
      selectedPlayers = firstTimePlayers.slice(0, count);
    } else {
      // Take all first-time players and fill remaining with experienced players using SMART
      selectedPlayers = [...firstTimePlayers];
      
      const remainingSlots = count - selectedPlayers.length;
      if (remainingSlots > 0 && experiencedPlayers.length > 0) {
        // Use SMART scoring for experienced players
        const scoredExperienced = experiencedPlayers.map(player => ({
          ...player,
          smartScore: this.calculatePlayerScore(player, allPlayers)
        }));
        
        scoredExperienced.sort((a, b) => b.smartScore - a.smartScore);
        selectedPlayers.push(...scoredExperienced.slice(0, remainingSlots));
      }
    }
    
    console.log('Initial phase selection:', selectedPlayers.map(p => `${p.name}(G:${p.gamesPlayed || 0})`).join(', '));
    return selectedPlayers.slice(0, count);
  }

  // ADAPTIVE WAIT DISTRIBUTION: Core innovation for dynamic player counts
  calculateAdaptiveWaitDistribution(benchSize) {
    console.log(`Calculating adaptive wait distribution for bench size ${benchSize}`);
    
    // During initial phase, use more conservative distribution to avoid long waits
    if (this.initialPhaseActive) {
      console.log('Initial phase: Using conservative wait distribution');
      if (benchSize <= 4) {
        return [1, 1, 1, 1]; // Everyone waits 1 game
      } else if (benchSize <= 6) {
        return [1, 1, 2, 2]; // Very conservative
      } else {
        return [1, 2, 2, 3]; // Slightly more variation but still conservative
      }
    }
    
    // Full SMART phase: Use standard adaptive distribution
    let distribution;
    
    if (benchSize <= 6) {
      distribution = [1, 1, 2, 2]; // Capacity: 6
    } else if (benchSize <= 9) {
      distribution = [1, 2, 3, 3]; // Capacity: 9
    } else if (benchSize <= 12) {
      distribution = [1, 2, 3, 4]; // Capacity: 10
    } else {
      distribution = [1, 2, 4, 5]; // Capacity: 12
    }
    
    const capacity = distribution.reduce((sum, w) => sum + w, 0);
    
    if (capacity > benchSize) {
      console.error(`CAPACITY VIOLATION: Distribution [${distribution.join(',')}] requires ${capacity} but bench is ${benchSize}`);
      distribution = [1, 1, 1, 1];
    }
    
    console.log(`Adaptive distribution: [${distribution.join(',')}] (capacity: ${capacity}/${benchSize})`);
    return distribution;
  }

  // Select players from bench using enhanced SMART algorithm (for full SMART phase)
  selectFromBench(bench, count, allPlayers) {
    if (bench.length <= count) {
      return [...bench];
    }
    
    // Check for players with guaranteed inclusion (1-game wait completion)
    const guaranteedPlayers = bench.filter(player => {
      const waitHistory = player.waitHistory || [];
      return waitHistory.length > 0 && 
             waitHistory[waitHistory.length - 1] === 1 &&
             player.gamesUntilEligible === 0 &&
             player.lastGameEnd === this.gameNumber - 1;
    });
    
    console.log(`Guaranteed players (1-game wait completion): ${guaranteedPlayers.map(p => p.name).join(', ')}`);
    
    let selectedPlayers = [...guaranteedPlayers];
    const remainingSlots = count - selectedPlayers.length;
    
    if (remainingSlots > 0) {
      // Fill remaining slots using SMART scoring
      const otherEligible = bench.filter(player => 
        !guaranteedPlayers.some(guaranteed => guaranteed.id === player.id)
      );
      
      const scoredPlayers = otherEligible.map(player => ({
        ...player,
        smartScore: this.calculatePlayerScore(player, allPlayers)
      }));
      
      scoredPlayers.sort((a, b) => b.smartScore - a.smartScore);
      selectedPlayers.push(...scoredPlayers.slice(0, remainingSlots));
    }
    
    return selectedPlayers.slice(0, count);
  }

  // Enhanced player scoring for selection
  calculatePlayerScore(player, allPlayers) {
    const waitTime = this.gameNumber - (player.lastGameEnd || 0);
    const gamesPlayed = player.gamesPlayed || 0;
    const avgGames = allPlayers.length > 0 ? 
      allPlayers.reduce((sum, p) => sum + (p.gamesPlayed || 0), 0) / allPlayers.length : 0;
    
    let score = 0;
    
    // Factor 1: Wait Time Fairness (primary - 150 points per game)
    score += waitTime * 150;
    
    // Factor 2: Games Balance (50 points)
    score += Math.max(0, avgGames - gamesPlayed) * 50;
    
    // Factor 3: Wait Experience Balance (25 points for players with less varied wait experience)
    const avgHistoricalWait = this.getAverageWaitTime(player);
    const sessionAvg = this.getSessionAverageWaitTime(allPlayers);
    if (avgHistoricalWait < sessionAvg) {
      score += 25;
    }
    
    // Factor 4: Emergency Override (1000 points for 4+ game waits)
    if (waitTime >= 4) {
      score += 1000; // MUST be selected
    }
    
    // Factor 5: Initial bench player bonus (during transition period)
    if (this.initialBenchPlayers.has(player.id) && gamesPlayed === 0) {
      score += 500; // High priority for first-time players
    }
    
    return score;
  }

  // Assign optimal partnerships with rating constraints
  assignOptimalPartnerships(players) {
    if (!players || players.length !== 4) {
      return { players: players || [], partnerships: null, formatted: 'Not enough players' };
    }
    
    // Generate all possible team pairings
    const pairings = [
      [[players[0], players[1]], [players[2], players[3]]], // A+B vs C+D
      [[players[0], players[2]], [players[1], players[3]]], // A+C vs B+D
      [[players[0], players[3]], [players[1], players[2]]]  // A+D vs B+C
    ];
    
    // Score each pairing
    const scoredPairings = pairings.map(pairing => ({
      pairing,
      score: this.scorePairing(pairing),
      isValid: this.validatePairing(pairing)
    }));
    
    // Filter valid pairings and select best
    const validPairings = scoredPairings.filter(sp => sp.isValid);
    
    if (validPairings.length === 0) {
      console.warn('No valid pairings found, using best available');
      const bestPairing = scoredPairings.sort((a, b) => b.score - a.score)[0];
      return {
        players: players,
        partnerships: bestPairing.pairing,
        formatted: this.formatPairing(bestPairing.pairing)
      };
    }
    
    const bestPairing = validPairings.sort((a, b) => b.score - a.score)[0];
    
    console.log(`Best pairing: ${this.formatPairing(bestPairing.pairing)} (Score: ${bestPairing.score})`);
    
    // Track partnerships
    this.trackPartnerships(bestPairing.pairing);
    
    return {
      players: players,
      partnerships: bestPairing.pairing,
      formatted: this.formatPairing(bestPairing.pairing)
    };
  }

  // Validate pairing meets rating constraints
  validatePairing(pairing) {
    if (!pairing || pairing.length !== 2) return false;
    
    const [team1, team2] = pairing;
    const team1Combined = (team1[0].currentRating || team1[0].rating || 3.5) + 
                         (team1[1].currentRating || team1[1].rating || 3.5);
    const team2Combined = (team2[0].currentRating || team2[0].rating || 3.5) + 
                         (team2[1].currentRating || team2[1].rating || 3.5);
    
    const combinedDiff = Math.abs(team1Combined - team2Combined);
    
    return combinedDiff <= 1.0; // Combined rating differential must be ≤ 1.0
  }

  // Score a specific pairing
  scorePairing(pairing) {
    if (!pairing || pairing.length !== 2) return -1000;
    
    const [team1, team2] = pairing;
    let score = 0;
    
    // Partnership novelty scoring
    const p1Key = this.getPartnershipKey(team1[0], team1[1]);
    const p2Key = this.getPartnershipKey(team2[0], team2[1]);
    
    const p1Count = this.sessionPartnerships.get(p1Key) || 0;
    const p2Count = this.sessionPartnerships.get(p2Key) || 0;
    
    // Bonus for new partnerships
    if (p1Count === 0) score += 100;
    if (p2Count === 0) score += 100;
    
    // Penalty for repeats
    score -= p1Count * 40;
    score -= p2Count * 40;
    
    // Rating balance bonus
    const team1Combined = (team1[0].currentRating || team1[0].rating || 3.5) + 
                         (team1[1].currentRating || team1[1].rating || 3.5);
    const team2Combined = (team2[0].currentRating || team2[0].rating || 3.5) + 
                         (team2[1].currentRating || team2[1].rating || 3.5);
    
    const combinedDiff = Math.abs(team1Combined - team2Combined);
    score += Math.max(0, 50 - (combinedDiff * 50)); // Bonus for balanced teams
    
    return score;
  }

  // Track partnerships in session
  trackPartnerships(pairing) {
    const [team1, team2] = pairing;
    
    const p1Key = this.getPartnershipKey(team1[0], team1[1]);
    const p2Key = this.getPartnershipKey(team2[0], team2[1]);
    
    this.sessionPartnerships.set(p1Key, (this.sessionPartnerships.get(p1Key) || 0) + 1);
    this.sessionPartnerships.set(p2Key, (this.sessionPartnerships.get(p2Key) || 0) + 1);
    
    console.log(`Partnerships tracked: ${p1Key}, ${p2Key}`);
  }

  // Wait time tracking methods
  recordWaitTime(playerId, waitTime) {
    if (!this.playerWaitHistory.has(playerId)) {
      this.playerWaitHistory.set(playerId, []);
    }
    this.playerWaitHistory.get(playerId).push(waitTime);
  }

  getAverageWaitTime(player) {
    const history = this.playerWaitHistory.get(player.id) || [];
    if (history.length === 0) return 0;
    return history.reduce((sum, wait) => sum + wait, 0) / history.length;
  }

  getSessionAverageWaitTime(allPlayers) {
    let totalWait = 0;
    let totalEntries = 0;
    
    allPlayers.forEach(player => {
      const history = this.playerWaitHistory.get(player.id) || [];
      totalWait += history.reduce((sum, wait) => sum + wait, 0);
      totalEntries += history.length;
    });
    
    return totalEntries > 0 ? totalWait / totalEntries : 2.5; // Default average
  }

  // Utility methods
  getPartnershipKey(player1, player2) {
    return [player1.id, player2.id].sort().join('-');
  }

  formatPairing(pairing) {
    if (!pairing || pairing.length !== 2) return 'No partnerships';
    const [team1, team2] = pairing;
    return `${team1[0].name} + ${team1[1].name} vs ${team2[0].name} + ${team2[1].name}`;
  }

  // Standard rotation manager methods
  insertIntoBench(remainingBench, exitingPlayers) {
    // In dynamic system, bench management is handled by GameInterface
    return remainingBench;
  }

  incrementWaitTimes(bench) {
    // Wait time management is handled by GameInterface with adaptive algorithm
    if (!bench) return [];
    return bench.filter(p => p && p.id).map(player => ({
      ...player,
      waitTime: (player.waitTime || 0) + 1
    }));
  }

  nextGame() {
    this.gameNumber++;
    console.log(`${this.initialPhaseActive ? 'Initial Phase' : 'Full SMART'} Game ${this.gameNumber} starting`);
  }

  getSessionStats() {
    const partnershipCounts = Array.from(this.sessionPartnerships.values());
    const repeatedPartnerships = partnershipCounts.filter(count => count > 1).length;
    
    return {
      uniquePartnerships: this.sessionPartnerships.size,
      repeatedPartnerships: repeatedPartnerships,
      totalGames: this.gameNumber - 1,
      avgPartnershipsPerPlayer: this.sessionPartnerships.size > 0 ? 
        partnershipCounts.reduce((sum, count) => sum + count, 0) / this.sessionPartnerships.size : 0,
      initialPhaseActive: this.initialPhaseActive,
      initialBenchPlayersRemaining: this.initialBenchPlayers.size
    };
  }

  reset() {
    this.sessionPartnerships.clear();
    this.playerWaitHistory.clear();
    this.committedNextUp = null;
    this.gameNumber = 1;
    
    // Reset initial phase tracking
    this.initialBenchPlayers.clear();
    this.initialPhaseActive = true;
    
    console.log('Dynamic SMART Manager reset with initial phase tracking');
  }

  // Validate session configuration for dynamic player counts
  validateSessionConfiguration(courtCount, playerCount) {
    const minPlayers = courtCount * 4 + 5; // 4C + 5
    const maxPlayers = courtCount * 8;     // 8C
    
    if (playerCount < minPlayers) {
      throw new Error(`SMART rotation requires minimum ${minPlayers} players for ${courtCount} courts (4C+5)`);
    }
    
    if (playerCount > maxPlayers) {
      throw new Error(`Maximum ${maxPlayers} players allowed for ${courtCount} courts (8C limit)`);
    }
    
    const benchSize = playerCount - (courtCount * 4);
    console.log(`Valid configuration: ${courtCount} courts, ${playerCount} players, ${benchSize} bench`);
    
    return {
      isValid: true,
      benchSize,
      waitDistribution: this.calculateAdaptiveWaitDistribution(benchSize)
    };
  }

  // Get recommended player counts for given court count
  static getRecommendedPlayerCounts(courtCount) {
    return {
      minimum: courtCount * 4 + 5,  // 4C + 5
      optimal: courtCount * 6,      // 6C sweet spot
      maximum: courtCount * 8       // 8C limit
    };
  }
}

export default SmartBenchManager;